import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface EducationalSchemaProps {
  /** Page name (e.g., "ABC & Numbers"). */
  name: string;
  /** Short description (used in schema + OG). */
  description: string;
  /** Large image URL (>= 1200px wide preferred) for Google Discover. */
  image?: string;
  /** Subjects covered (e.g., ["Phonics", "Counting"]). */
  about?: string[];
  /** Default: "preschool, kindergarten, early elementary". */
  educationalLevel?: string;
  /** Default: "LearningResource". */
  resourceType?: "LearningResource" | "Course" | "CreativeWork";
  /** Author name for E-E-A-T (default TinyMinds Editorial Team). */
  author?: string;
  /** ISO date — defaults to today, used as dateModified for freshness. */
  dateModified?: string;
  /** ISO date for first publish. */
  datePublished?: string;
}

/**
 * Injects educational content schema (Course / LearningResource) plus
 * Google Discover hints: large image OG/Twitter tags, author trust signals
 * and fresh `dateModified` to encourage frequent crawling and indexing.
 */
const EducationalSchema = ({
  name,
  description,
  image = "https://tinyminds.in/og-image.png",
  about = [],
  educationalLevel = "preschool, kindergarten, early elementary",
  resourceType = "LearningResource",
  author = "TinyMinds Editorial Team",
  dateModified,
  datePublished = "2024-01-01",
}: EducationalSchemaProps) => {
  const { pathname } = useLocation();
  const url = `https://tinyminds.in${pathname}`;
  const modified = dateModified ?? new Date().toISOString();

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": resourceType,
    name,
    description,
    url,
    inLanguage: "en-IN",
    isAccessibleForFree: true,
    learningResourceType: "Interactive Activity",
    educationalLevel,
    educationalUse: "instruction, assessment, practice",
    interactivityType: "active",
    typicalAgeRange: "3-8",
    countryOfOrigin: { "@type": "Country", "name": "India" },
    spatialCoverage: { "@type": "Country", "name": "India" },
    audience: [
      {
        "@type": "EducationalAudience",
        audienceType: "children ages 3-8 in India",
        educationalRole: "student",
        geographicArea: { "@type": "Country", name: "India" },
      },
      {
        "@type": "EducationalAudience",
        audienceType: "Indian parents and preschool teachers",
        educationalRole: "parent",
      },
    ],
    teaches: about.length > 0 ? about : undefined,
    about: about.map((a) => ({ "@type": "Thing", name: a })),
    image: {
      "@type": "ImageObject",
      url: image,
      width: 1200,
      height: 630,
      caption: name,
    },
    author: {
      "@type": "Organization",
      name: author,
      url: "https://tinyminds.in/about",
    },
    publisher: {
      "@type": "Organization",
      name: "TinyMinds",
      logo: {
        "@type": "ImageObject",
        url: "https://tinyminds.in/icons/icon-192.png",
        width: 192,
        height: 192,
      },
    },
    datePublished,
    dateModified: modified,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  // Course type also benefits from provider + hasCourseInstance hints
  if (resourceType === "Course") {
    schema.provider = {
      "@type": "EducationalOrganization",
      name: "TinyMinds",
      sameAs: "https://tinyminds.in",
    };
    schema.hasCourseInstance = {
      "@type": "CourseInstance",
      courseMode: "online",
      inLanguage: "en-IN",
    };
  }

  return (
    <Helmet>
      {/* Google Discover: large image + fresh signals */}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={name} />
      <meta property="og:title" content={name} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="article:author" content={author} />
      <meta property="article:publisher" content="TinyMinds" />
      <meta property="article:modified_time" content={modified} />
      <meta property="article:published_time" content={datePublished} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:title" content={name} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:label1" content="Reading time" />
      <meta name="twitter:data1" content="5 min" />
      <meta name="twitter:label2" content="Author" />
      <meta name="twitter:data2" content={author} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </Helmet>
  );
};

export default EducationalSchema;
